<h2
  id="website-accessibility-statement-inline-with-public-sector-body-websites-and-mobile-applications-no-2-accessibility-regulations-2018">
  Website accessibility statement inline with Public Sector Body (Websites and Mobile Applications) (No. 2)
  Accessibility Regulations 2018
</h2>

<p>
  This accessibility statement applies to
  <a href="https://subscriptions.edina.ac.uk/">https://subscriptions.edina.ac.uk/</a>
</p>

<p>
  This website is run by the University of Edinburgh. We want as many people as possible to be able to use this
  application. For example, that means you should be able to:
</p>
<ul>
  <li>Using your browser settings, change colours, contrast levels and fonts</li>
  <li>Zoom in up to 200% without the text spilling off the screen</li>
  <li>Navigate most of the website using just a keyboard</li>
  <li>Navigate most of the website using speech recognition software</li>
</ul>

<p>
  We’ve also made the website text as simple as possible to understand. However, some of our content is technical, and
  we use technical terms where there is no easier wording we could use without changing what the text means.
</p>

<h2 id="customising-the-website">Customising the website</h2>

<p>
  AbilityNet has advice on making your device easier to use if you have a disability. This is an external site with
  suggestions to make your computer more accessible:
</p>

<p><a href="https://mcmw.abilitynet.org.uk/">AbilityNet - My Computer My Way</a></p>

<p>
  With a few simple steps you can customise the appearance of our website using your browser settings to make it easier
  to read and navigate:
</p>

<p>
  <a href="https://www.ed.ac.uk/about/website/accessibility/customising-site"
    >Additional information on how to customise our website appearance</a
  >
</p>

<p>
  If you are a member of University staff or a student, you can use the free SensusAccess accessible document conversion
  service:
</p>

<p>
  <a href="https://www.ed.ac.uk/student-disability-service/staff/supporting-students/accessible-technology"
    >Information on SensusAccess</a
  >
</p>

<h2 id="how-accessible-this-website-is">How accessible this website is</h2>

<p>We know some parts of this website are not fully accessible:</p>

<ul>
  <li>Screenreaders may not be able to fully navigate the site</li>
  <li>Assistive technology functionality may not work on all parts of the site</li>
  <li>Users need to scroll to access specific content</li>
  <li>Not all colour contrasts meet the recommended Web Content Accessibility Guidelines 2.2 AA standard</li>
</ul>

<h2 id="feedback-and-contact-information">Feedback and contact information</h2>

<p>
  If you need information on this website in a different format, including accessible PDF, large print, audio recording
  or braille:
</p>

<ul>
  <li>Email <a href="mailto:edina@ed.ac.uk">edina&#64;ed.ac.uk</a></li>
  <li>Telephone +44 (0)131 650 3302</li>
  <li>
    British Sign Language (BSL) users can contact us via
    <a href="http://contactscotland-bsl.org/">contactSCOTLAND-BSL</a>, the on-line BSL interpreting service.
  </li>
</ul>

<p>We will consider your request and get back to you in 5 working days.</p>

<h2 id="reporting-accessibility-problems-with-this-website">Reporting accessibility problems with this website</h2>

<p>
  We are always looking to improve the accessibility of this website. If you find any problems not listed on this page,
  or think we’re not meeting accessibility requirements, please contact:
</p>

<ul>
  <li>Email <a href="mailto:edina@ed.ac.uk">edina&#64;ed.ac.uk</a></li>
  <li>Telephone +44 (0)131 650 3302</li>
  <li>
    British Sign Language (BSL) users can contact us via
    <a href="http://contactscotland-bsl.org/">contactSCOTLAND-BSL</a>, the on-line BSL interpreting service.
  </li>
</ul>

<p>We will consider your request and get back to you in 5 working days.</p>

<h2 id="enforcement-procedure">Enforcement procedure</h2>

<p>
  The Equality and Human Rights Commission (EHRC) is responsible for enforcing the Public Sector Bodies (Websites and
  Mobile Applications) (No. 2) Accessibility Regulations 2018 (the ‘accessibility regulations’). If you’re not happy
  with how we respond to your complaint please contact the Equality Advisory and Support Service (EASS) directly:
</p>

<p>
  <a href="https://www.equalityadvisoryservice.com/"
    >Contact details for the Equality Advisory and Support Service (EASS)</a
  >
</p>

<p>The government has produced information on how to report accessibility issues:</p>

<p>
  <a href="https://www.gov.uk/reporting-accessibility-problem-public-sector-website"
    >Reporting an accessibility problem on a public sector website</a
  >
</p>

<h3 id="contacting-us-by-phone-using-british-sign-language">Contacting us by phone using British Sign Language</h3>

<p>British Sign Language service</p>

<p>
  contactSCOTLAND-BSL runs a service for British Sign Language users and all of Scotland’s public bodies using video
  relay. This enables sign language users to contact public bodies and vice versa. The service operates 24 hours a day,
  7 days a week.
</p>

<p><a href="https://contactscotland-bsl.org/">contactSCOTLAND-BSL service details</a></p>

<h2 id="technical-information-about-this-websites-accessibility">
  Technical information about this website’s accessibility
</h2>

<p>
  The University of Edinburgh is committed to making its websites and applications accessible, in accordance with the
  Public Sector Bodies (Websites and Mobile Applications) (No. 2) Accessibility Regulations 2018.
</p>

<p>
  This website is partially compliant with the Web Content Accessibility Guidelines (WCAG) 2.2 AA standard, due to the
  non-compliances listed below.
</p>

<p>The full guidelines are available at:</p>

<p><a href="https://www.w3.org/TR/WCAG22/">Web Content Accessibility Guidelines (WCAG) 2.2 AA standard</a></p>

<h2 id="non-accessible-content">Non accessible content</h2>

<p>The content listed below is non-accessible for the following reasons.</p>

<h3 id="noncompliance-with-the-accessibility-regulations">Noncompliance with the accessibility regulations</h3>

<p>The following items do not comply with the WCAG 2.2 AA success criteria:</p>

<ul>
  <li>
    Content implemented using markup languages cannot always be read by assistive technologies
    <ul>
      <li><a href="https://www.w3.org/TR/WCAG22/#text-spacing">1.4.12 - Text spacing</a></li>
    </ul>
  </li>
  <li>
    Tooltips are not present for all icons and images
    <ul>
      <li><a href="https://www.w3.org/TR/WCAG22/#content-on-hover-or-focus">1.4.13 - Content on Hover or Focus</a></li>
    </ul>
  </li>
  <li>
    Screen readers are not able to identify some parts of the page
    <ul>
      <li><a href="https://www.w3.org/TR/WCAG22/#name-role-value">4.1.2 - Name, Role, Value</a></li>
    </ul>
  </li>
</ul>

<p>
  We aim to improve our website's accessibility on a regular and continuous basis. See the section below ('What we're
  doing to improve accessibility') on how we are improving our site accessibility.
</p>
<p>
  Where possible we are working towards solving these problems and expect improvements by September 2026. The site is
  fully within our control.
</p>

<h2 id="disproportionate-burden">Disproportionate burden</h2>

<p>We are not currently claiming that any accessibility problems would be a disproportionate burden to fix.</p>

<h3 id="pdfs-and-other-documents">PDFs and other documents</h3>

<p>
  Many of our older PDFs and Word documents do not necessarily meet accessibility standards, for example, and they may
  not be easy to view using a screen reader. The accessibility regulations
  <a href="http://www.legislation.gov.uk/uksi/2018/952/regulation/4/made"
    >do not require us to fix PDFs or other documents published before 23 September 2019</a
  >
  if they’re not essential to providing our services. We will also try to ensure any new PDFs or Word documents we
  publish will meet accessibility standards, and where we find any that are not accessible, we will rectify this as soon
  as possible.
</p>

<h2 id="what-were-doing-to-improve-accessibility">What we’re doing to improve accessibility</h2>

<p>
  We will continue to address and make adequate improvements to the accessibility issues highlighted. Unless specified
  otherwise, a complete solution or improvement will be in place by September 2026.
</p>
<p>
  While we are in the process of resolving these accessibility issues we will ensure reasonable adjustments are in place
  to make sure no user is disadvantaged. As changes are made, we will continue to review accessibility and retest the
  accessibility of this website.
</p>

<h2 id="preparation-of-this-accessibility-statement">Preparation of this accessibility statement</h2>

<p><strong>This statement was prepared on the 17th July 2025.</strong></p>

<p>
  The website was last tested on 30th June 2025. The testing was carried out by the Software Engineering team within the
  Information Services EDINA directorate at the University of Edinburgh using both automated and manual methods. The
  site was tested on a PC, primarily using Microsoft Edge alongside Mozilla Firefox and Google Chrome.
</p>
<p>
  Recent world-wide usage levels survey for different screen readers and browsers shows that Chrome, Mozilla Firefox and
  Microsoft Edge are increasing in popularity and Google Chrome is now the favoured browser for screen readers:
</p>
<p>
  <a href="https://webaim.org/projects/screenreadersurvey9/">WebAIM: Screen Reader User Survey</a>
</p>
<p>The aforementioned three browsers have been used in certain questions for reasons of breadth and variety.</p>
<p>
  We ran automated testing using
  <a href="https://developer.chrome.com/docs/lighthouse/">Google Chrome’s lighthouse accessibility tool</a>
  and then manual testing that included:
</p>

<ul>
  <li>Spell check functionality;</li>
  <li>Scaling using different resolutions and reflow;</li>
  <li>Options to customise the interface (magnification, font, background colour, etc);</li>
  <li>Keyboard navigation and keyboard traps;</li>
  <li>Data validation;</li>
  <li>Warning of links opening in new tab or window;</li>
  <li>Information conveyed in the colour or sound only;</li>
  <li>Flashing, moving or scrolling text;</li>
  <li>Operability if JavaScript is disabled;</li>
  <li>
    Use with screen reading software (using the
    <a href="https://www.freedomscientific.com/products/software/jaws/">JAWS screen reader</a>);
  </li>
  <li>Tooltips and text alternatives for any non-text content;</li>
  <li>Time limits;</li>
  <li>Compatibility with mobile accessibility functionality (Android and iOS).</li>
</ul>
